<template>
  <div :class="['report-basic-toc', { sticky: isSticky }, 'border-top', 'border-bottom']">
    <b-container class="px-0">
      <div class="menu-container">
        <div class="categories-wrapper">
          <div class="categories">
            <div
              v-for="(category, c) in report_categories"
              :key="`category-${category.category_id}-${c}`"
              class="category-button"
              @click="selectCategory(category.category_id)"
            >
              <span
                class="f-120 fw-700 hover-transparent-dark"
                :class="[
                  'button-default',
                  { 'button-active': selectedCategoryId === category.category_id },
                ]"
                @click="selectCategory(category.category_id)"
                :data-id="category.category_id"
              >
                {{ category.category_name }}
              </span>
            </div>

            <div v-if="categoryType === 'sasb'">
              <div
                v-for="subcategory in subcategories"
                :key="`subcategory-${subcategory.id}`"
                class="subcategory-button"
                @click="selectSubcategory(subcategory.id)"
              >
                <span
                  class="f-120 fw-700 hover-transparent-dark"
                  :class="[
                    'button-default',
                    { 'button-active': selectedSubcategoryId === subcategory.id },
                  ]"
                >
                  {{ subcategory.name }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-container>
  </div>
</template>

<script>
  import { mapState, mapActions, mapGetters } from 'vuex';

  export default {
    name: 'ContentsListNew',
    components: {},
    props: {
      categoryType: {
        type: String,
        default: 'gri',
      },
    },
    data() {
      return {
        initialCategories: [],
        gri_categories_loaded: false,
        selectedIndustryKey: null,
        selectedSubcategoryId: null,
        subcategories: [],
        isSticky: false,
      };
    },
    computed: {
      ...mapState('report_contents', ['sasb_contents']),
      ...mapState('report_list', ['lang']),
      ...mapGetters('gri2021_report_view', [
        'report_universal_categories',
        'report_topic_specific_categories',
      ]),
      ...mapGetters('report_list', ['current_report_code', 'selected_report']),
      ...mapGetters('category', ['selectedCategoryId']),
      report_categories() {
        if (this.categoryType === 'tcfd') {
          return [{ category_id: 'tcfd', category_name: 'TCFD' }];
        } else if (this.categoryType === 'data center') {
          return [{ category_id: 'data-center', category_name: 'Data Center' }];
        } else if (this.categoryType === 'sasb') {
          return Object.keys(this.sasb_contents).map((key) => ({
            category_id: key,
            category_name: this.sasb_contents[key]?.industry_name || '',
            category_name_e: this.sasb_contents[key]?.industry_name || '',
          }));
        } else if (this.categoryType === 'gri' && this.gri_categories_loaded) {
          let categories = [];
          if (this.report_universal_categories) {
            categories = categories.concat(
              this.report_universal_categories.map((category) => ({
                category_id: category.category_id,
                category_name: category.category_name,
                category_name_e: category.category_name_e,
              }))
            );
          }
          if (this.report_topic_specific_categories) {
            categories = categories.concat(
              this.report_topic_specific_categories.map((category) => ({
                category_id: category.category_id,
                category_name: category.category_name,
                category_name_e: category.category_name_e,
              }))
            );
          }
          return categories;
        } else {
          return [];
        }
      },
    },
    methods: {
      ...mapActions('report_contents', ['fetchSelectedSasb']),
      ...mapActions('category', ['setSelectedCategoryId']),
      checkContentsListHeight() {
        this.$nextTick(() => {
          const contentsListElement = this.$el.querySelector('.report-basic-toc');
          if (contentsListElement) {
            const contentsListHeight = contentsListElement.offsetHeight;
            this.isSticky = contentsListHeight > 80;
          }
        });
      },

      handleResize() {
        this.checkContentsListHeight();
      },
      selectCategory(categoryId) {
        this.setSelectedCategoryId(categoryId);
        const queryParam =
          this.categoryType === 'sasb' ? { industry_key: categoryId } : { categoryId };
        this.$router.push({
          query: { ...this.$route.query, ...queryParam },
        });
      },
      selectSubcategory(subcategoryId) {
        this.selectedSubcategoryId = subcategoryId;
      },
      selectInitialCategory() {
        if (this.initialCategories.length > 0) {
          this.selectCategory(this.initialCategories[0].category_id);
        }
      },
      loadInitialCategories() {
        if (this.categoryType !== 'sasb') {
          let categories = [];
          if (this.report_universal_categories) {
            categories = categories.concat(
              this.report_universal_categories.map((category) => ({
                category_id: category.category_id,
                category_name: category.category_name,
                category_name_e: category.category_name,
              }))
            );
          }
          if (this.report_topic_specific_categories) {
            categories = categories.concat(
              this.report_topic_specific_categories.map((category) => ({
                category_id: category.category_id,
                category_name: category.category_name,
                category_name_e: category.category_name,
              }))
            );
          }
          this.initialCategories = categories;
          this.gri_categories_loaded = true;
          if (!this.$route.query.categoryId && categories.length > 0) {
            this.selectCategory(categories[0].category_id);
          }
        }
      },
      loadIndustryKeyData(industryKey) {
        if (this.categoryType === 'sasb') {
          this.selectedIndustryKey = industryKey;
          this.selectedIndustryData = this.sasb_contents[industryKey];
          if (this.selectedIndustryData) {
            this.subcategories = this.selectedIndustryData.subcategories || [];
          }
        }
      },
    },
    watch: {
      sasb_contents(newList) {
        if (this.categoryType === 'sasb' && Object.keys(newList).length > 0) {
          const firstCategoryId = Object.keys(this.sasb_contents)[0];
          this.selectCategory(firstCategoryId);
          this.loadIndustryKeyData(this.selectedIndustryKey);
        }
      },
      '$route.query.t': {
        handler(newType) {
          this.categoryType = newType || 'gri';
          if (this.categoryType === 'sasb') {
            this.fetchSelectedSasb(this.current_report_code);
          } else {
            this.loadInitialCategories();
          }
        },
        immediate: true,
      },
      '$route.query.industry_key': {
        handler(newKey) {
          if (this.categoryType === 'sasb') {
            this.loadIndustryKeyData(newKey);
          }
        },
        immediate: true,
      },
      '$route.query.categoryId': {
        handler(newCategoryId) {
          if (this.categoryType !== 'sasb') {
            this.setSelectedCategoryId(newCategoryId);
          }
        },
        immediate: true,
      },
      gri_categories_loaded(newVal) {
        if (newVal) {
          this.selectInitialCategory();
        }
      },
    },
    mounted() {
      if (this.categoryType === 'sasb') {
        this.selectedIndustryKey =
        this.$route.query.industry_key || Object.keys(this.sasb_contents)[0];
        this.fetchSelectedSasb(this.current_report_code);
      } else {
        this.loadInitialCategories();
      }
      this.checkContentsListHeight();
      window.addEventListener('resize', this.handleResize);
    },
    created() {
      this.categoryType = this.$route.query.t || 'gri';
      if (this.categoryType === 'sasb') {
        this.fetchSelectedSasb(this.current_report_code).then(() => {
          this.loadInitialCategories();
        });
      } else {
        this.loadInitialCategories();
      }

      this.$nextTick(() => {
        this.checkContentsListHeight();
      });
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.handleResize);
    },
  };
</script>

<style lang="scss" scoped>
  @import '~@fortawesome/fontawesome-free/css/all.css';

  .button-default {
    border: none !important;
    background-color: transparent !important;
    color: black !important;
    position: relative;
    font-weight: bold;
    font-size: 1.2rem;
    opacity: 0.5;
    cursor: pointer;

    &:hover {
      opacity: 1;
    }
  }

  .button-active {
    position: relative;
    opacity: 1 !important;
  }

  .report-basic-toc {
    position: relative;
    width: 100%;
    padding-top: 15px;
    padding-bottom: 15px;
    background-color: transparent;
  }

  .menu-container {
    display: flex;
    align-items: center;
    height: 30px;
  }

  .categories-wrapper {
    display: flex;
    flex-grow: 1;
    scroll-behavior: smooth;
  }

  .categories {
    display: flex;
    align-items: center;
    white-space: nowrap;
  }

  .category-button,
  .subcategory-button {
    margin-right: 1.5vw;
  }
</style>
